import React, { useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Users, Shield, X, Eye, Edit2, Save, ChevronDown, ChevronUp, Crown } from 'lucide-react';
import axios from 'axios';
import './BRTeamViewModal.css';

/**
 * Unified TeamViewModal for both BR (duo/squad) and MP (team vs team) tournaments
 * - BR: Shows team positions, allows editing squadmate preferences
 * - MP: Shows Team 1 vs Team 2 positions, allows captain to edit teammate preferences
 */
const TeamViewModal = ({ tournament, user, onClose, onUpdate, t }) => {
  const [editingTeammates, setEditingTeammates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showTeamPositions, setShowTeamPositions] = useState(false);

  // Determine tournament type
  const isBattleRoyale = tournament.gameMode === 'battleRoyale';
  const isBRDuoOrSquad = isBattleRoyale && ['duo', 'squad'].includes(tournament.multiplayerType);
  const isBRDuo = tournament.multiplayerType === 'duo';

  // Check if this is an MP team game (2v2, 4v4, etc.)
  const teamGameModes = ['teamDeathmatch', 'domination', 'searchAndDestroy', 'hardpoint', 'killConfirmed', 'captureTheFlag', 'frontline', 'cranked'];
  const isMPTeamGame = teamGameModes.includes(tournament.gameMode) && tournament.multiplayerType === 'team';

  // Calculate max teammates based on game type
  const teamSize = isMPTeamGame ? Math.ceil(tournament.maxPlayers / 2) : (isBRDuo ? 2 : 4);
  const maxTeammates = isMPTeamGame ? teamSize - 1 : (isBRDuo ? 1 : 3); // Exclude captain for MP

  // Get user's squad data (BR only)
  const userSquadData = useMemo(() => {
    if (!isBRDuoOrSquad || !tournament.brTeamData) return null;
    return tournament.brTeamData.find(td => {
      const tdUserId = (td.userId?._id || td.userId)?.toString();
      return tdUserId === user._id?.toString();
    });
  }, [tournament, user, isBRDuoOrSquad]);

  // Check if user is in a team (BR)
  const userBRTeamAssignment = useMemo(() => {
    if (!isBRDuoOrSquad || !tournament.brTeamAssignments) return null;
    return tournament.brTeamAssignments.find(a => {
      const aUserId = (a.userId?._id || a.userId)?.toString();
      return aUserId === user._id?.toString();
    });
  }, [tournament, user, isBRDuoOrSquad]);

  // Check if user is in a team (MP)
  const userMPTeamAssignment = useMemo(() => {
    if (!isMPTeamGame || !tournament.mpTeamAssignments) return null;
    return tournament.mpTeamAssignments.find(a => {
      const aUserId = (a.userId?._id || a.userId)?.toString();
      return aUserId === user._id?.toString();
    });
  }, [tournament, user, isMPTeamGame]);

  // Get user's team number for MP
  const userMPTeamNumber = userMPTeamAssignment?.teamNumber;
  const userMPTeamKey = userMPTeamNumber === 1 ? 'team1' : userMPTeamNumber === 2 ? 'team2' : null;

  // Get MP team settings for user's team
  const userMPTeamSettings = useMemo(() => {
    if (!isMPTeamGame || !userMPTeamKey || !tournament.mpTeamSettings) return null;
    return tournament.mpTeamSettings[userMPTeamKey] || null;
  }, [tournament, isMPTeamGame, userMPTeamKey]);

  // Check if user is captain of their MP team
  const isMPCaptain = useMemo(() => {
    if (!userMPTeamSettings) return false;
    const captainId = userMPTeamSettings.captainId?._id || userMPTeamSettings.captainId;
    return captainId?.toString() === user._id?.toString();
  }, [userMPTeamSettings, user]);

  // For MP: user can edit if they're captain OR if no captain is set yet
  const canEditMPTeammates = useMemo(() => {
    if (!isMPTeamGame || tournament.status !== 'waiting' || !userMPTeamAssignment) return false;
    // Can edit if captain or no captain set
    return isMPCaptain || !userMPTeamSettings?.captainId;
  }, [isMPTeamGame, tournament.status, userMPTeamAssignment, isMPCaptain, userMPTeamSettings]);

  // User can edit squadmates if they're in a BR team and tournament is waiting
  const canEditSquadmates = useMemo(() => {
    return isBRDuoOrSquad && tournament.status === 'waiting' && userBRTeamAssignment;
  }, [isBRDuoOrSquad, tournament, userBRTeamAssignment]);

  // Combined edit permission
  const canEdit = canEditSquadmates || canEditMPTeammates;

  // Check if user has 'squad' preference (BR) or 'specific' preference (MP)
  const hasSpecificPreference = isMPTeamGame
    ? userMPTeamSettings?.preference === 'specific'
    : userSquadData?.preference === 'squad';

  // Initialize editing teammates on mount
  useEffect(() => {
    if (isMPTeamGame && canEditMPTeammates) {
      // Initialize from MP team settings
      const currentTeammates = userMPTeamSettings?.teammates || [];
      const initialValues = Array.from({ length: maxTeammates }).map((_, i) => {
        const tm = currentTeammates[i];
        if (!tm) return '';
        if (tm.isRandom) return '__RANDOM__';
        return tm.name || '';
      });
      setEditingTeammates(initialValues);
    } else if (canEditSquadmates && userSquadData) {
      // Initialize from BR squadmates
      const currentSquadmates = userSquadData?.squadmates || [];
      const initialValues = Array.from({ length: maxTeammates }).map((_, i) => {
        const sm = currentSquadmates[i];
        if (!sm) return '';
        if (sm.isRandom) return '__RANDOM__';
        return sm.name || '';
      });
      setEditingTeammates(initialValues);
    }
  }, [canEditSquadmates, canEditMPTeammates, userSquadData, userMPTeamSettings, maxTeammates, isMPTeamGame]);

  // Calculate BR teams
  const brTeams = useMemo(() => {
    if (!isBRDuoOrSquad) return [];

    const teamCount = isBRDuo
      ? Math.ceil(tournament.maxPlayers / 2)
      : Math.ceil(tournament.maxPlayers / 4);

    const teamsArray = [];

    const getUserIdString = (assignment) => {
      if (!assignment || !assignment.userId) return null;
      return (assignment.userId._id || assignment.userId).toString();
    };

    for (let i = 0; i < teamCount; i++) {
      const teamNumber = i + 1;
      const teamMembers = (tournament.brTeamAssignments || [])
        .filter(assignment => assignment.teamNumber === teamNumber)
        .map(assignment => ({
          userId: getUserIdString(assignment),
          username: assignment.username,
          inGameName: assignment.inGameName,
          preference: assignment.preference,
          isCurrentUser: getUserIdString(assignment) === user._id.toString()
        }));

      const maxTeamSize = isBRDuo ? 2 : 4;
      const isFull = teamMembers.length >= maxTeamSize;
      const hasSoloPlayer = teamMembers.some(m => m.preference === 'solo');

      const hasSquadRestriction = teamMembers.some(m => {
        if (m.preference === 'squad' && m.userId !== user._id.toString()) {
          const squadData = tournament.brTeamData?.find(td => {
            const tdUserId = (td.userId._id || td.userId).toString();
            return tdUserId === m.userId;
          });
          if (squadData && squadData.squadmates && squadData.squadmates.length > 0) {
            return squadData.squadmates.every(sm => !sm.isRandom);
          }
        }
        return false;
      });

      teamsArray.push({
        teamNumber,
        members: teamMembers,
        isFull,
        hasSoloPlayer,
        hasSquadRestriction,
        isAvailable: !isFull && !hasSoloPlayer && !hasSquadRestriction,
        spotsRemaining: maxTeamSize - teamMembers.length
      });
    }

    return teamsArray;
  }, [tournament, user, isBRDuoOrSquad, isBRDuo]);

  // Calculate MP teams (Team 1 vs Team 2)
  const mpTeams = useMemo(() => {
    if (!isMPTeamGame) return [];

    const teamsArray = [];

    const getUserIdString = (assignment) => {
      if (!assignment || !assignment.userId) return null;
      return (assignment.userId._id || assignment.userId).toString();
    };

    for (let i = 1; i <= 2; i++) {
      const teamNumber = i;
      const teamKey = i === 1 ? 'team1' : 'team2';
      const teamSettings = tournament.mpTeamSettings?.[teamKey];
      const captainId = teamSettings?.captainId?._id || teamSettings?.captainId;

      const teamMembers = (tournament.mpTeamAssignments || [])
        .filter(assignment => assignment.teamNumber === teamNumber)
        .map(assignment => ({
          userId: getUserIdString(assignment),
          username: assignment.username,
          inGameName: assignment.inGameName,
          isCurrentUser: getUserIdString(assignment) === user._id.toString(),
          isCaptain: captainId?.toString() === getUserIdString(assignment)
        }));

      teamsArray.push({
        teamNumber,
        members: teamMembers,
        isFull: teamMembers.length >= teamSize,
        spotsRemaining: teamSize - teamMembers.length,
        captainId: captainId,
        preference: teamSettings?.preference || 'random',
        teammates: teamSettings?.teammates || []
      });
    }

    return teamsArray;
  }, [tournament, user, isMPTeamGame, teamSize]);

  // Use appropriate teams based on tournament type
  const teams = isBRDuoOrSquad ? brTeams : mpTeams;

  // Find user's team
  const userTeam = teams.find(team =>
    team.members.some(m => m.isCurrentUser)
  );

  // Toggle slot state for MP: locked -> random -> specific (editable) -> locked
  // For BR: empty -> random -> empty
  const toggleTeammateSlot = (index) => {
    const newTeammates = [...editingTeammates];
    const currentValue = newTeammates[index];

    if (isMPTeamGame) {
      // MP Team cycle: locked ('') -> random ('__RANDOM__') -> specific ('__SPECIFIC__') -> locked ('')
      // When specific, user can type a name in the input
      if (currentValue === '' || currentValue === '__LOCKED__') {
        newTeammates[index] = '__RANDOM__';
      } else if (currentValue === '__RANDOM__') {
        newTeammates[index] = '__SPECIFIC__';  // Now editable
      } else if (currentValue === '__SPECIFIC__') {
        newTeammates[index] = '';  // Back to locked
      } else {
        // Has a name - toggle to locked
        newTeammates[index] = '';
      }
    } else {
      // BR: Original behavior
      if (currentValue === '') {
        newTeammates[index] = '__RANDOM__';
      } else if (currentValue === '__RANDOM__') {
        newTeammates[index] = '';
      } else {
        newTeammates[index] = '__RANDOM__';
      }
    }

    setEditingTeammates(newTeammates);
  };

  // Update teammate name
  const handleTeammateChange = (index, value) => {
    const newTeammates = [...editingTeammates];
    newTeammates[index] = value;
    setEditingTeammates(newTeammates);
  };

  // Save teammates
  const handleSaveTeammates = async () => {
    try {
      setSaving(true);
      setError('');

      const formattedTeammates = editingTeammates.map(tm => {
        if (tm === '__RANDOM__') return { name: '', isRandom: true };
        if (tm === '' || tm === '__LOCKED__' || tm === '__SPECIFIC__') {
          // For MP: empty slot or unfilled specific = locked (isLocked: true)
          // For BR: empty slot = null (filtered out)
          if (isMPTeamGame) {
            return { name: '', isRandom: false, isLocked: true };
          }
          return null;
        }
        return { name: tm.trim(), isRandom: false, isLocked: false };
      }).filter(tm => tm !== null);

      // Use appropriate endpoint based on game type
      const endpoint = isMPTeamGame
        ? `/api/tournaments/${tournament._id}/update-mp-teammates`
        : `/api/tournaments/${tournament._id}/update-br-squadmates`;

      const payload = isMPTeamGame
        ? { teammates: formattedTeammates }
        : { squadmates: formattedTeammates };

      const response = await axios.post(endpoint, payload);

      if (response.data.tournament && onUpdate) {
        onUpdate(response.data.tournament);
      }

      setError('');
      // Close modal after successful save
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error updating teammates:', err);
      setError(err.response?.data?.message || 'Failed to update teammates');
      setSaving(false);
    }
  };

  // Modal title based on type
  const getModalTitle = () => {
    if (canEdit) {
      return t('editTeamPreferences', 'Edit Team Preferences');
    }
    return t('viewTeamPositions', 'Team Positions');
  };

  // Get label for editing section
  const getEditSectionLabel = () => {
    if (isMPTeamGame) {
      return t('editAllowedTeammates', 'Edit Allowed Teammates');
    }
    return t('editAllowedSquadmates', 'Edit Allowed Squadmates');
  };

  return ReactDOM.createPortal(
    <div className="br-view-modal-overlay">
      <div className="br-view-modal">
        {/* Header */}
        <div className="br-view-modal-header">
          <div className="br-view-modal-icon">
            <Edit2 className="w-6 h-6 text-white" />
          </div>
          <h3 className="br-view-modal-title">
            {getModalTitle()}
          </h3>
          <button onClick={onClose} className="br-view-modal-close" disabled={saving}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User's Team Info */}
        {userTeam && (
          <div className="br-view-modal-user-team">
            <Shield className="w-5 h-5 text-green-400" />
            <span>
              {t('yourTeam', 'Your Team')}: <strong>{t('team', 'Team')} {isMPTeamGame ? userTeam.teamNumber : String(userTeam.teamNumber).padStart(2, '0')}</strong>
            </span>
            {isMPTeamGame && (isMPCaptain || !userMPTeamSettings?.captainId) && (
              <span className="ml-2 text-yellow-400 flex items-center gap-1">
                <Crown className="w-4 h-4" /> {t('captain', 'Captain')}
              </span>
            )}
          </div>
        )}

        {/* Teammates Editing Section */}
        {canEdit && (
          <div className="br-view-squadmates-section">
            <div className="br-view-squadmates-header">
              <Users className="w-5 h-5 text-purple-400" />
              <span>{getEditSectionLabel()}</span>
            </div>

            <div className="br-view-preference-info">
              {hasSpecificPreference ? (
                <span className="br-view-preference-badge squad">
                  👥 {isMPTeamGame
                    ? t('specificPreference', 'Specific Mode - Only named players can join')
                    : t('squadPreference', 'Squad Mode - Specific teammates only')}
                </span>
              ) : (
                <span className="br-view-preference-badge random">
                  🎲 {t('randomPreference', 'Random Mode - Anyone can join')}
                </span>
              )}
            </div>

            <div className="br-view-squadmates-edit">
              <p className="br-view-edit-hint">
                {isMPTeamGame
                  ? t('mpTeammatesHint', 'Enter player names to allow specific players. Use 🎲 for random players. Empty slots are locked.')
                  : t('squadmatesHint', 'Enter the names of players allowed to join your team. Leave empty or set to random to allow anyone.')}
              </p>

              {error && (
                <div className="br-view-error">
                  {error}
                </div>
              )}

              {Array.from({ length: maxTeammates }).map((_, index) => {
                const value = editingTeammates[index] || '';
                const isRandom = value === '__RANDOM__';
                const isSpecificEditable = value === '__SPECIFIC__';
                const isEmpty = value === '' || value === '__LOCKED__';
                const isLocked = isMPTeamGame && isEmpty;
                const hasName = !isRandom && !isSpecificEditable && !isEmpty && value.length > 0;

                return (
                  <div key={index} className="br-view-squadmate-slot">
                    <span className="br-view-slot-label">{t('slot', 'Slot')} {index + 1}</span>
                    <div className="br-view-squadmate-input-group">
                      <input
                        type="text"
                        value={isRandom ? '' : (isEmpty ? '' : (isSpecificEditable ? '' : value))}
                        onChange={(e) => handleTeammateChange(index, e.target.value)}
                        placeholder={
                          isRandom ? t('randomPlayer', '🎲 Random Player') :
                          isLocked ? t('lockedSlot', '🔒 Locked Slot') :
                          isSpecificEditable ? t('enterPlayerName', 'Enter player name...') :
                          isEmpty ? t('enterPlayerName', 'Enter player name...') :
                          t('playerName', 'Player Name')
                        }
                        className={`br-view-squadmate-input ${isRandom ? 'random' : isLocked ? 'locked' : isSpecificEditable ? 'specific-editable' : isEmpty ? 'empty' : 'specific'}`}
                        disabled={saving || isRandom || isLocked}
                      />
                      <button
                        type="button"
                        onClick={() => toggleTeammateSlot(index)}
                        className={`br-view-squadmate-toggle ${isRandom ? 'is-random' : isLocked ? 'is-locked' : isSpecificEditable ? 'is-specific-editable' : isEmpty ? 'is-empty' : 'is-specific'}`}
                        disabled={saving}
                        title={
                          isMPTeamGame
                            ? (isLocked ? t('unlockSlot', 'Click to allow random') :
                               isRandom ? t('setSpecific', 'Click to set specific player') :
                               isSpecificEditable ? t('lockSlot', 'Click to lock slot') :
                               hasName ? t('lockSlot', 'Click to lock slot') : t('setRandom', 'Set to Random'))
                            : (isEmpty ? t('setRandom', 'Set to Random') : isRandom ? t('clearSlot', 'Clear Slot') : t('setRandom', 'Set to Random'))
                        }
                      >
                        {isMPTeamGame
                          ? (isLocked ? '🎲' : isRandom ? '✏️' : isSpecificEditable ? '🔒' : hasName ? '🔒' : '🎲')
                          : (isEmpty ? '🎲' : isRandom ? '❌' : '🎲')}
                      </button>
                    </div>
                    <span className={`br-view-slot-status ${isLocked ? 'locked-status' : ''}`}>
                      {isRandom ? t('anyoneCanJoin', 'Anyone can join this slot') :
                       isLocked ? t('lockedSlotInfo', '🔒 Locked - no one can join') :
                       isSpecificEditable ? t('enterNameInfo', '✏️ Enter a player name') :
                       isEmpty ? t('emptySlotInfo', 'Empty - no one can use this slot') :
                       t('specificPlayer', 'Only this player can join')}
                    </span>
                  </div>
                );
              })}

              <div className="br-view-edit-actions">
                <button
                  onClick={handleSaveTeammates}
                  disabled={saving}
                  className="br-view-save-btn"
                >
                  {saving ? (
                    <>{t('saving', 'Saving...')}</>
                  ) : (
                    <><Save className="w-4 h-4" /> {t('saveChanges', 'Save Changes')}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Teams Grid */}
        <div className="br-view-modal-content">
          {isBRDuoOrSquad && (
            <button
              className="br-view-teams-toggle"
              onClick={() => setShowTeamPositions(!showTeamPositions)}
            >
              <Eye className="w-4 h-4" />
              <span>{t('viewAllTeamPositions', 'View All Team Positions')}</span>
              {showTeamPositions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}

          {(isMPTeamGame || showTeamPositions) && (
          <>
            {/* MP Team Game: Show Team 1 vs Team 2 layout */}
            {isMPTeamGame ? (
              <div className="mp-teams-view">
                <div className="mp-teams-versus">
                  {teams.map((team, idx) => (
                    <React.Fragment key={team.teamNumber}>
                      <div className={`mp-team-view-card ${team.members.some(m => m.isCurrentUser) ? 'user-team' : ''}`}>
                        <div className="mp-team-view-header" style={{ color: team.teamNumber === 1 ? '#60a5fa' : '#f87171' }}>
                          <Shield className="w-5 h-5" />
                          <span>{t('team', 'Team')} {team.teamNumber}</span>
                          {team.preference === 'specific' && (
                            <span className="ml-2 text-xs bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded">
                              {t('specific', 'SPECIFIC')}
                            </span>
                          )}
                        </div>
                        <div className="mp-team-view-members">
                          {team.members.map((member, mIdx) => (
                            <div
                              key={mIdx}
                              className={`mp-team-view-member ${member.isCurrentUser ? 'current-user' : ''}`}
                            >
                              <Users className="w-4 h-4" />
                              <span>
                                {member.inGameName || member.username}
                                {member.isCurrentUser && ` (${t('you', 'You')})`}
                              </span>
                              {member.isCaptain && (
                                <Crown className="w-4 h-4 text-yellow-400 ml-auto" title={t('captain', 'Captain')} />
                              )}
                            </div>
                          ))}
                          {Array.from({ length: team.spotsRemaining }).map((_, eIdx) => (
                            <div key={`empty-${eIdx}`} className="mp-team-view-member empty">
                              <Users className="w-4 h-4" />
                              <span>{t('emptySlot', 'Empty Slot')}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mp-team-view-status">
                          {team.isFull ? (
                            <span className="status-full">✅ {t('teamFull', 'Full')}</span>
                          ) : (
                            <span className="status-available">
                              ⚡ {team.spotsRemaining} {t('spotsRemaining', 'spots remaining')}
                            </span>
                          )}
                        </div>
                      </div>
                      {idx === 0 && (
                        <div className="mp-vs-divider-large">
                          <span>VS</span>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ) : (
              /* BR Teams Grid */
              <div className="br-view-teams-grid">
                {teams.map(team => (
                  <div
                    key={team.teamNumber}
                    className={`br-view-team-card ${
                      team.members.some(m => m.isCurrentUser) ? 'user-team' : ''
                    }`}
                  >
                    <div className="br-view-team-card-header">
                      <div className="br-view-team-number">
                        <Shield className="w-4 h-4" />
                        {t('team', 'Team')} {String(team.teamNumber).padStart(2, '0')}
                      </div>
                      {team.members.some(m => m.isCurrentUser) && (
                        <div className="br-view-team-your-badge">
                          {t('yourTeam', 'YOUR TEAM')}
                        </div>
                      )}
                    </div>

                    <div className="br-view-team-members">
                      {team.members.map((member, idx) => (
                        <div
                          key={idx}
                          className={`br-view-team-member ${member.isCurrentUser ? 'current-user' : ''}`}
                        >
                          <Users className="w-3 h-3" />
                          <span>
                            {member.inGameName || member.username}
                            {member.isCurrentUser && ` (${t('you', 'You')})`}
                          </span>
                          {member.preference === 'solo' && (
                            <span className="br-view-member-badge solo">SOLO</span>
                          )}
                          {member.preference === 'squad' && (
                            <span className="br-view-member-badge squad">SQUAD</span>
                          )}
                        </div>
                      ))}
                      {Array.from({ length: team.spotsRemaining }).map((_, idx) => (
                        <div key={`empty-${idx}`} className="br-view-team-member empty">
                          <Users className="w-3 h-3" />
                          <span>{t('emptySlot', 'Empty Slot')}</span>
                        </div>
                      ))}
                    </div>

                    <div className="br-view-team-status">
                      {team.isFull && (
                        <span className="status-full">✅ {t('teamFull', 'Full')}</span>
                      )}
                      {team.hasSoloPlayer && !team.isFull && (
                        <span className="status-solo">🔒 {t('soloPlayer', 'Solo Player')}</span>
                      )}
                      {team.hasSquadRestriction && !team.isFull && (
                        <span className="status-squad">🔒 {t('squadOnly', 'Squad Only')}</span>
                      )}
                      {team.isAvailable && (
                        <span className="status-available">
                          ⚡ {t('available', 'Available')} ({team.spotsRemaining} {t('spots', 'spots')})
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            <div className="br-view-modal-summary">
              <div className="br-view-summary-item">
                <span className="br-view-summary-label">{t('totalTeams', 'Total Teams')}:</span>
                <span className="br-view-summary-value">{teams.length}</span>
              </div>
              <div className="br-view-summary-item">
                <span className="br-view-summary-label">{t('playersAssigned', 'Players Assigned')}:</span>
                <span className="br-view-summary-value">
                  {teams.reduce((sum, t) => sum + t.members.length, 0)} / {tournament.maxPlayers}
                </span>
              </div>
              <div className="br-view-summary-item">
                <span className="br-view-summary-label">{t('fullTeams', 'Full Teams')}:</span>
                <span className="br-view-summary-value">{teams.filter(t => t.isFull).length}</span>
              </div>
            </div>
          </>
          )}
        </div>

        {/* Close Button */}
        <div className="br-view-modal-actions">
          <button
            onClick={onClose}
            disabled={saving}
            className="br-view-btn br-view-btn-close"
          >
            {t('close', 'Close')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TeamViewModal;
